import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const LABEL_SELECTOR = ".optionSectionLabel";
const SOURCE_SELECTOR = 'span[class~="group-hover:block"]';
const TOOLTIP_WIDTH = 200;
const TOOLTIP_GAP = 10;
const VIEWPORT_PADDING = 8;

const findLabel = (target, container) => {
  const label = target?.closest?.(LABEL_SELECTOR);
  return label && container?.contains(label) ? label : null;
};

const findTooltipText = (label) => {
  const source = Array.from(label?.children || []).find((child) =>
    child.matches?.(SOURCE_SELECTOR),
  );
  return source?.textContent?.replace(/\s+/g, " ").trim() || "";
};

export default function LayoutHelpTooltipLayer({ targetRef, watchKey }) {
  const tooltipRef = useRef(null);
  const [active, setActive] = useState(null);
  const [position, setPosition] = useState(null);

  const updatePosition = useCallback(() => {
    const anchor = active?.anchor;
    const container = targetRef.current;
    if (!anchor || !container || !document.body.contains(anchor)) {
      setPosition(null);
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const intersectsContainer =
      anchorRect.bottom >= containerRect.top &&
      anchorRect.top <= containerRect.bottom &&
      anchorRect.right >= containerRect.left &&
      anchorRect.left <= containerRect.right;

    if (!intersectsContainer) {
      setPosition(null);
      return;
    }

    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    const width = tooltipRect?.width || TOOLTIP_WIDTH;
    const height = tooltipRect?.height || 48;
    const maxLeft = Math.max(
      VIEWPORT_PADDING,
      window.innerWidth - width - VIEWPORT_PADDING,
    );
    const maxTop = Math.max(
      VIEWPORT_PADDING,
      window.innerHeight - height - VIEWPORT_PADDING,
    );

    let left = anchorRect.left - width - TOOLTIP_GAP;
    if (left < VIEWPORT_PADDING) left = anchorRect.right + TOOLTIP_GAP;
    left = Math.min(Math.max(left, VIEWPORT_PADDING), maxLeft);

    const centeredTop = anchorRect.top + (anchorRect.height - height) / 2;
    const top = Math.min(
      Math.max(centeredTop, VIEWPORT_PADDING),
      maxTop,
    );

    setPosition({ left, top });
  }, [active, targetRef]);

  useEffect(() => {
    const container = targetRef.current;
    if (!container) return undefined;

    const openFromTarget = (target) => {
      const label = findLabel(target, container);
      if (!label) return;
      const text = findTooltipText(label);
      if (text) {
        setActive((current) =>
          current?.anchor === label && current.text === text
            ? current
            : { anchor: label, text },
        );
      }
    };

    const closeFromTarget = (target, relatedTarget) => {
      const label = findLabel(target, container);
      if (!label || label.contains(relatedTarget)) return;
      setActive((current) =>
        current?.anchor === label ? null : current,
      );
    };

    const handleMouseOver = (event) => openFromTarget(event.target);
    const handleMouseOut = (event) =>
      closeFromTarget(event.target, event.relatedTarget);
    const handleFocusIn = (event) => openFromTarget(event.target);
    const handleFocusOut = (event) =>
      closeFromTarget(event.target, event.relatedTarget);

    container.addEventListener("mouseover", handleMouseOver);
    container.addEventListener("mouseout", handleMouseOut);
    container.addEventListener("focusin", handleFocusIn);
    container.addEventListener("focusout", handleFocusOut);

    return () => {
      container.removeEventListener("mouseover", handleMouseOver);
      container.removeEventListener("mouseout", handleMouseOut);
      container.removeEventListener("focusin", handleFocusIn);
      container.removeEventListener("focusout", handleFocusOut);
      setActive(null);
    };
  }, [targetRef, watchKey]);

  useLayoutEffect(() => {
    if (!active) {
      setPosition(null);
      return undefined;
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [active, updatePosition]);

  if (!active || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      className="layout-help-tooltip"
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden",
      }}
    >
      {active.text}
    </div>,
    document.body,
  );
}
